import { useState, type CSSProperties, type ReactNode } from "react";
import { Tab, vars } from "@pack-it-up/ui-web";
import type { FriendRequest, UserProfile } from "@pack-it-up/core";
import { useStoreValue } from "@pack-it-up/viewmodel/bindings";
import { useSession } from "../../app/SessionProvider.js";
import { ManagedProfiles } from "../settings/ManagedProfiles.js";
import { removeAction } from "./FriendsPage.css.js";

// ── Avatar helpers ────────────────────────────────────────────────────────────
// Kit uses per-user gradient avatars (av-sm, 30px). We derive a deterministic
// gradient from a seed string so each person gets a consistent colour pair.
const AVATAR_GRADIENTS: ReadonlyArray<[string, string]> = [
  ["#3b82f6", "#a855f7"],
  ["#22c55e", "#3b82f6"],
  ["#ef4444", "#a855f7"],
  ["#f0a500", "#ef4444"],
  ["#22c55e", "#f0a500"],
  ["#ec4899", "#a855f7"],
  ["#f0a500", "#22c55e"],
  ["#3b82f6", "#22c55e"],
];

const avatarGradient = (seed: string): string => {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = ((h * 31) + seed.charCodeAt(i)) >>> 0;
  const pair = AVATAR_GRADIENTS[h % AVATAR_GRADIENTS.length]!;
  return `linear-gradient(135deg, ${pair[0]}, ${pair[1]})`;
};

const initialsOf = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) return (parts[0]![0]! + parts[parts.length - 1]![0]!).toUpperCase();
  return name.slice(0, 2).toUpperCase();
};

// ── Mini design-token shorthands for this file only ──────────────────────────
// Pages use vars (CSS-variable references set by ThemeProvider) for theme-aware
// styling. This alias avoids `vars.color.foo` repetition in inline styles.
const c = vars.color;
const sp = vars.space;
const r = vars.radius;

// ── Shared sub-components (page-private) ─────────────────────────────────────

// Small circular avatar matching the kit's .av-sm (30px).
const AvatarSm = ({ seed, name }: { seed: string; name: string }): ReactNode => (
  <div
    aria-hidden="true"
    style={{
      width: 30,
      height: 30,
      borderRadius: "50%",
      background: avatarGradient(seed),
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 11,
      fontWeight: 600,
      color: "#fff",
      flexShrink: 0,
    }}
  >
    {initialsOf(name)}
  </div>
);

// Stacked name + email block.
const PersonInfo = ({ name, email }: { name: string; email?: string }): ReactNode => (
  <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
    <div style={{ fontSize: 13, fontWeight: 500 }}>{name}</div>
    {email !== undefined && (
      <div style={{ fontSize: 11, color: c.textMuted, marginTop: 1 }}>{email}</div>
    )}
  </div>
);

// Kit .tm-btn / .req-* buttons — small bordered action buttons.
type ActionVariant = "accept" | "decline" | "remove" | "cancel";
// "remove" gets its colors from the `removeAction` CSS class instead (danger colors +
// hover need a real stylesheet rule — inline styles can't express `:hover`).
const ACTION_STYLES: Record<Exclude<ActionVariant, "remove">, CSSProperties> = {
  accept: { background: vars.color.success, color: "#fff", border: "none" },
  decline: { background: "transparent", color: vars.color.textMuted, border: `1px solid ${vars.color.border}` },
  cancel:  { background: "transparent", color: vars.color.textMuted, border: `1px solid ${vars.color.border}` },
};
const ActionBtn = ({ label, onClick, variant }: { label: string; onClick: () => void; variant: ActionVariant }): ReactNode => (
  <button
    type="button"
    onClick={onClick}
    className={variant === "remove" ? removeAction : undefined}
    style={{
      fontSize: 12,
      fontWeight: 500,
      padding: `${sp.xs} ${sp.sm}`,
      borderRadius: r.xs,
      cursor: "pointer",
      fontFamily: vars.font.body,
      whiteSpace: "nowrap",
      ...(variant === "remove" ? {} : ACTION_STYLES[variant]),
    }}
  >
    {label}
  </button>
);

// A row within the bordered card (kit .friend-req-row).
const CardRow = ({
  name,
  email,
  seed,
  trailing,
  first,
}: {
  name: string;
  email?: string;
  seed: string;
  trailing: ReactNode;
  first: boolean;
}): ReactNode => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: sp.md,
      padding: `${sp.md} ${sp.lg}`,
      borderTop: first ? "none" : `1px solid ${c.border}`,
    }}
  >
    <AvatarSm seed={seed} name={name} />
    <PersonInfo name={name} {...(email !== undefined ? { email } : {})} />
    {trailing}
  </div>
);

// ── Tab IDs ───────────────────────────────────────────────────────────────────
type FriendsTab = "current" | "incoming" | "outgoing" | "managed";

// ── FriendsPage ───────────────────────────────────────────────────────────────

// The friends screen (Doc 04 §6): kit-parity restyle (tracker task #13).
// Layout mirrors the kit: search-to-request bar → result row → Friends/Requests/Sent
// tabs → bordered card with per-tab panels.
// Constraint: backend is exact-email lookup only — placeholder adjusted.
export const FriendsPage = (): ReactNode => {
  const { friends, managed } = useSession();
  const myFriends = useStoreValue(friends, (s) => s.friends);
  const incoming  = useStoreValue(friends, (s) => s.incoming);
  const outgoing  = useStoreValue(friends, (s) => s.outgoing);
  const lookupResult = useStoreValue(friends, (s) => s.lookupResult);
  const error = useStoreValue(friends, (s) => s.error);
  const managedProfiles = useStoreValue(managed, (s) => s.profiles);

  const [email, setEmail] = useState("");
  const [activeTab, setActiveTab] = useState<FriendsTab>("current");

  const handleLookup = (): void => {
    const trimmed = email.trim();
    if (trimmed) void friends.getState().lookupByEmail(trimmed);
  };

  const handleAddFriend = (): void => {
    if (lookupResult) {
      void friends.getState().sendRequest(lookupResult.userId);
      setEmail("");
    }
  };

  const profileOf = (r: FriendRequest, side: "from" | "to") =>
    side === "from" ? r.fromProfile : r.toProfile;

  return (
    <div
      style={{
        flex: 1,
        overflowY: "auto",
        padding: `${sp.xl} 28px 60px`,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
      }}
    >
      <div style={{ width: "100%", maxWidth: 520 }}>

        {/* ── Search bar (kit .friend-search) ─────────────────────────── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: sp.sm,
            background: c.background,
            border: `1px solid ${c.border}`,
            borderRadius: r.sm,
            padding: `${sp.sm} ${sp.md}`,
            color: c.textMuted,
          }}
        >
          {/* Search icon */}
          <svg width={14} height={14} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
            <circle cx={11} cy={11} r={8} />
            <path d="m21 21-4.35-4.35" />
          </svg>
          <input
            type="email"
            value={email}
            aria-label="Search by email to send a request"
            placeholder="Search by email to send a request…"
            autoComplete="off"
            onChange={(e) => {
              setEmail(e.target.value);
              // Clear stale result when the email changes.
            }}
            onKeyDown={(e) => { if (e.key === "Enter") handleLookup(); }}
            style={{
              flex: 1,
              background: "transparent",
              border: "none",
              outline: "none",
              color: "inherit",
              fontFamily: vars.font.body,
              fontSize: 13,
            }}
          />
        </div>

        {/* ── Error ────────────────────────────────────────────────────── */}
        {error !== null && (
          <div role="alert" style={{ fontSize: 12, color: c.danger, marginTop: sp.xs, paddingLeft: 4 }}>
            {error}
          </div>
        )}

        {/* ── Lookup result (kit .friend-row → .friend-add) ────────────── */}
        {lookupResult !== null && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 10,
              padding: sp.sm,
              borderRadius: r.sm,
              marginTop: sp.xs,
              cursor: "pointer",
            }}
            onClick={handleAddFriend}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") handleAddFriend(); }}
            aria-label={`Add ${lookupResult.displayName} as a friend`}
          >
            <AvatarSm seed={lookupResult.userId} name={lookupResult.displayName} />
            <PersonInfo name={lookupResult.displayName} />
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleAddFriend(); }}
              style={{
                marginLeft: "auto",
                fontSize: 12,
                color: c.accent,
                fontWeight: 500,
                background: "transparent",
                border: "none",
                cursor: "pointer",
                fontFamily: vars.font.body,
              }}
            >
              + Add friend
            </button>
          </div>
        )}

        {/* ── Tab bar (kit .tabs, no horizontal padding) ───────────────── */}
        <div
          role="tablist"
          aria-label="Friends"
          style={{
            display: "flex",
            borderBottom: `1px solid ${c.border}`,
            marginTop: sp.xl,
          }}
        >
          <Tab active={activeTab === "current"} count={myFriends.length} onClick={() => setActiveTab("current")}>
            Friends
          </Tab>
          <Tab active={activeTab === "incoming"} count={incoming.length} onClick={() => setActiveTab("incoming")}>
            Requests
          </Tab>
          <Tab active={activeTab === "outgoing"} count={outgoing.length} onClick={() => setActiveTab("outgoing")}>
            Sent
          </Tab>
          <Tab active={activeTab === "managed"} count={managedProfiles.length} onClick={() => setActiveTab("managed")}>
            Managed profiles
          </Tab>
        </div>

        {/* ── Managed profiles panel — its own card styling (ManagedProfiles renders
            bordered per-profile cards), not the list-row card below. ────────────── */}
        {activeTab === "managed" && (
          <div style={{ marginTop: 14 }}>
            <ManagedProfiles />
          </div>
        )}

        {/* ── Card (kit .settings-card) ─────────────────────────────────── */}
        {activeTab !== "managed" && (
        <div
          style={{
            background: c.surface,
            border: `1px solid ${c.border}`,
            borderRadius: r.lg,
            overflow: "hidden",
            marginTop: 14,
          }}
        >
          {/* Friends panel */}
          {activeTab === "current" && (
            myFriends.length === 0 ? (
              <p style={{ padding: `${sp.xl} ${sp.lg}`, textAlign: "center", fontSize: 12, color: c.textMuted, margin: 0 }}>
                No friends yet — find someone by email above.
              </p>
            ) : (
              myFriends.map((f: UserProfile, i) => (
                <CardRow
                  key={f.userId}
                  seed={f.userId}
                  name={f.displayName}
                  email={f.email}
                  first={i === 0}
                  trailing={
                    <div style={{ marginLeft: "auto" }}>
                      <ActionBtn
                        label="Remove"
                        variant="remove"
                        onClick={() => void friends.getState().removeFriend(f.userId)}
                      />
                    </div>
                  }
                />
              ))
            )
          )}

          {/* Requests (incoming) panel */}
          {activeTab === "incoming" && (
            incoming.length === 0 ? (
              <p style={{ padding: `${sp.xl} ${sp.lg}`, textAlign: "center", fontSize: 12, color: c.textMuted, margin: 0 }}>
                No pending requests.
              </p>
            ) : (
              incoming.map((req, i) => {
                const profile = profileOf(req, "from");
                const name = profile?.displayName ?? "Someone";
                const seed = profile?.userId ?? req.id;
                return (
                  <CardRow
                    key={req.id}
                    seed={seed}
                    name={name}
                    first={i === 0}
                    trailing={
                      <div style={{ display: "flex", gap: sp.xs, marginLeft: "auto" }}>
                        <ActionBtn
                          label="Accept"
                          variant="accept"
                          onClick={() => void friends.getState().acceptRequest(req.id)}
                        />
                        <ActionBtn
                          label="Decline"
                          variant="decline"
                          onClick={() => void friends.getState().ignoreRequest(req.id)}
                        />
                      </div>
                    }
                  />
                );
              })
            )
          )}

          {/* Sent (outgoing) panel */}
          {activeTab === "outgoing" && (
            outgoing.length === 0 ? (
              <p style={{ padding: `${sp.xl} ${sp.lg}`, textAlign: "center", fontSize: 12, color: c.textMuted, margin: 0 }}>
                No sent requests.
              </p>
            ) : (
              outgoing.map((req, i) => {
                const profile = profileOf(req, "to");
                const name = profile?.displayName ?? "Pending";
                const seed = profile?.userId ?? req.id;
                return (
                  <CardRow
                    key={req.id}
                    seed={seed}
                    name={name}
                    first={i === 0}
                    trailing={
                      <div style={{ display: "flex", alignItems: "center", gap: sp.xs, marginLeft: "auto" }}>
                        <span style={{ fontSize: 12, color: c.textMuted }}>Pending</span>
                        {/* Cancel calls ignoreRequest, which sets status→ignored, removing
                            the request from the outgoing stream (filtered by status=pending). */}
                        <ActionBtn
                          label="Cancel"
                          variant="cancel"
                          onClick={() => void friends.getState().ignoreRequest(req.id)}
                        />
                      </div>
                    }
                  />
                );
              })
            )
          )}
        </div>
        )}
      </div>
    </div>
  );
};
